/**
 * Rafraîchissement discret des collections du bureau.
 *
 * Un pointage, une demande de congé, une photo ou un rapport faits par un
 * ouvrier sur son téléphone n'apparaissaient au bureau qu'après un
 * rechargement complet de la page. Ce module donne le rythme : les
 * collections synchronisées (`useSyncedCollection`) s'y abonnent et relisent
 * le serveur quand l'onglet redevient visible, quand la fenêtre reprend la
 * main, quand on change d'écran, et toutes les minutes tant que l'onglet est
 * visible — jamais quand il est caché, où personne ne regarde.
 *
 * Il ne fait que prévenir : chaque collection décide elle-même de ne rien
 * toucher si une saisie est en cours, et ne se réécrit que si le serveur a
 * quelque chose de nouveau.
 */

/** Rythme du rafraîchissement périodique, onglet visible. */
export const INTERVALLE_MS = 60_000;

/**
 * Espace minimum entre deux rafraîchissements déclenchés par un geste —
 * changement d'écran, retour sur l'onglet : enchaîner trois écrans en cinq
 * secondes ne relit pas trois fois toute la base.
 */
export const DELAI_MINIMUM_MS = 10_000;

const abonnes = new Set();
let installe = false;
let minuteur = null;
let dernier = 0;

/** Vrai si l'onglet est visible, ou si l'on n'est pas dans un navigateur. */
function visible() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function previent() {
  if (!visible()) return;
  dernier = Date.now();
  for (const ecouteur of [...abonnes]) {
    try {
      ecouteur();
    } catch (e) {
      // Une collection qui échoue n'empêche pas les autres de se relire.
    }
  }
}

/** Repart d'un tour complet : la minute se compte depuis maintenant. */
function arme() {
  if (minuteur) clearInterval(minuteur);
  minuteur = setInterval(previent, INTERVALLE_MS);
}

function surGeste() {
  if (!visible() || Date.now() - dernier < DELAI_MINIMUM_MS) return;
  previent();
  arme();
}

function installeLesEcouteurs() {
  if (installe || typeof window === 'undefined') return;
  installe = true;
  document.addEventListener('visibilitychange', surGeste);
  window.addEventListener('focus', surGeste);
  arme();
}

/**
 * Abonne une collection au rythme du rafraîchissement.
 *
 * @param {() => void} ecouteur appelé à chaque rafraîchissement
 * @returns {() => void} fonction qui retire l'abonnement
 */
export function surRafraichissement(ecouteur) {
  abonnes.add(ecouteur);
  installeLesEcouteurs();
  return () => {
    abonnes.delete(ecouteur);
  };
}

/**
 * Rafraîchit à l'occasion d'un geste : le retour sur un écran.
 *
 * Sans effet si un rafraîchissement vient d'avoir lieu, ou si l'onglet est
 * caché.
 */
export function rafraichitAuRetour() {
  surGeste();
}

/**
 * Rafraîchit sans attendre : après une action qui a changé les données au
 * serveur hors des collections — un effacement de données personnelles, par
 * exemple —, l'écran doit refléter la base sans délai.
 */
export function rafraichit() {
  previent();
  arme();
}
